import * as React from "react";
import { ChevronDown, CloudCog } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useGetVideoQuery } from "../store/videoApi";
import { useSelector } from "react-redux";
import { RootState } from "@/types";
import { toast } from "sonner";
// import swc from "../assets/logo.png"
import robotwala from "../assets/robotwala1.png";
import { getYoutubeId } from "@/lib/utils";

const ViewLectures = () => {
  const [selectedTech, setSelectedTech] = React.useState("all");
  const [selectedDate, setSelectedDate] = React.useState(null);
  const [showAllDates, setShowAllDates] = React.useState(true);

  const { user } = useSelector((state: RootState) => state.user);
  console.log(user, "user");

  // Call the API query whenever the selected technology changes
  const { data: lectures, error } = useGetVideoQuery(
    {
      batch: user?.batch?._id,
      batchModule: selectedTech === "all" ? "" : selectedTech,
      Token: user?.token,
    },
    { skip: !user?.batch?._id },
  );
  console.log("Lectures API Response:", lectures);

  React.useEffect(() => {
    if (selectedTech !== "all" && error) {
      toast.error(error?.data?.message || "Failed to fetch lectures");
    }
  }, [error, selectedTech]);

  // Filter lectures based on selected technology and date
  const filteredLectures = (Array.isArray(lectures) ? lectures : [])?.filter(
    (lecture) => {
      const techMatch =
        selectedTech === "all" || lecture.batchModule === selectedTech;

      const dateMatch =
        showAllDates ||
        (selectedDate &&
          lecture.createdAt &&
          new Date(lecture.createdAt).toDateString() ===
            selectedDate.toDateString());

      return techMatch && dateMatch;
    },
  );

  // Handle technology change to fetch data based on the selected technology
  const handleTechChange = (tech) => {
    setSelectedTech(tech);
  };

  return (
    <div className="w-full max-w-6xl mx-auto p-4 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>
            <span className=" text-xl">View Lectures</span>
          </CardTitle>
          <CardDescription>
            Filter lectures by technology and date
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            {/* Technology Select */}
            <Select value={selectedTech} onValueChange={handleTechChange}>
              <SelectTrigger className="w-full md:w-[200px]">
                <SelectValue placeholder="Select Technology" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="all">SELECT TECHNOLOGY</SelectItem>
                  <SelectItem value="HTML">HTML</SelectItem>
                  <SelectItem value="CSS">CSS</SelectItem>
                  <SelectItem value="JavaScript">JavaScript</SelectItem>
                  <SelectItem value="React">React</SelectItem>
                  <SelectItem value="Backend">Backend</SelectItem>
                  <SelectItem value="Basic">Basic</SelectItem>
                  <SelectItem value="Intermediate">Intermediate</SelectItem>
                  <SelectItem value="Advanced">Advanced</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>

            {/* Date Selection */}
            <div className="flex gap-2 items-center">
              <Button
                className="bg-red-500"
                variant={showAllDates ? "default" : "outline"}
                onClick={() => setShowAllDates(true)}
              >
                All Dates
              </Button>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    className="w-32 sm:w-auto"
                    variant={!showAllDates ? "default" : "outline"}
                  >
                    {selectedDate ? selectedDate.toDateString() : "Select Date"}
                    <ChevronDown className="sm:ml-2 h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[230px] sm:w-auto max-w-full p-0 overflow-auto"
                  sideOffset={4}
                  align="start"
                >
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => {
                      setSelectedDate(date);
                      setShowAllDates(false);
                    }}
                    className="w-full max-w-sm"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lecture Grid */}
      <div className="sm:p-4 p-1">
        {filteredLectures?.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-300">
            No lectures uploaded yet.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredLectures?.map((lecture) => (
              <Card
                key={lecture?._id}
                className="p-4 border rounded shadow-md bg-white dark:bg-gray-800 flex flex-col gap-3 relative"
              >
                {/* Badge */}
                {lecture?.batchModule && (
                  <span className="absolute top-2 right-2 bg-red-500 text-white text-xs font-semibold py-1 px-3 rounded-full">
                    {lecture.batchModule}
                  </span>
                )}

                {/* Video Preview */}
                {lecture?.url ? (
                  <div className="aspect-w-16 aspect-h-9 rounded overflow-hidden">
                    <iframe
                      className="w-full h-full"
                      src={`https://www.youtube.com/embed/${getYoutubeId(lecture?.url)}`}
                      title={lecture?.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <div className="text-center text-gray-500 dark:text-gray-300">
                    No video available
                  </div>
                )}

                {/* Title + Uploader Info */}
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center space-x-2">
                    <img
                      src={robotwala}
                      alt={lecture?.title}
                      className="rounded-full w-11 h-9"
                    />
                    <div className="flex flex-col">
                      <h3 className="font-semibold text-base text-gray-800 dark:text-gray-100">
                        {lecture?.title}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Uploaded by: {lecture?.uploadedBy?.name}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Uploaded on:{" "}
                        {new Date(lecture?.createdAt).toLocaleDateString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          },
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewLectures;
